import type { ChartAnalyticsMetric } from '@/types/dashboard'
import type { CandleList } from '@/types/generated/public_api'

export type ChartAnalyticsPoint = {
  time: number
  value: number
}

export type ChartAnalyticsSeries = {
  metric: ChartAnalyticsMetric
  points: ChartAnalyticsPoint[]
}

export const CHART_ANALYTICS_DATA_KEYS: Partial<Record<ChartAnalyticsMetric, keyof CandleList>> = {
  vwap: 'vwap',
  twap: 'twap',
  liqwap: 'liqwap',
  volumeBase: 'volumeBase',
  volumeQuote: 'volumeQuote',
  buyVolumeBase: 'buyVolumeBase',
  sellVolumeBase: 'sellVolumeBase',
  netBuyVolumeBase: 'netBuyVolumeBase',
  netBuyVolumeQuote: 'netBuyVolumeQuote',
  deltaPercentage: 'deltaPercentage',
  totalLiqBase: 'totalLiqBase',
  totalLiqQuote: 'totalLiqQuote',
  liqTakerVolume: 'liqTakerVolume',
  liqMakerVolume: 'liqMakerVolume',
  totalLiqCount: 'totalLiqCount',
  netLiquidations: 'netLiquidations',
  totalTradeCount: 'totalTradeCount',
  buyTradeCount: 'buyTradeCount',
  sellTradeCount: 'sellTradeCount',
  netBuyTradeCount: 'netBuyTradeCount',
  avgTradeSize: 'avgTradeSize',
  avgBuyTradeSize: 'avgBuyTradeSize',
  avgSellTradeSize: 'avgSellTradeSize',
  parkinsonVolatility: 'realizedVolatility',
  vwapTwapSpread: 'vwapTwapSpread',
  volumeBaseBlockIncl: 'volumeBaseBlockIncl',
  volumeQuoteBlockIncl: 'volumeQuoteBlockIncl',
  buyVolumeBaseBlockIncl: 'buyVolumeBaseBlockIncl',
  sellVolumeBaseBlockIncl: 'sellVolumeBaseBlockIncl',
  netBuyVolumeBaseBlockIncl: 'netBuyVolumeBaseBlockIncl',
  netBuyVolumeQuoteBlockIncl: 'netBuyVolumeQuoteBlockIncl',
  openInterestBase: 'openInterestBaseClose',
  openInterestQuote: 'openInterestQuoteClose',
  aggregatedOpenInterest: 'openInterestQuoteClose',
  fundingRate: 'fundingRateClose',
  fundingApr: 'fundingAprClose',
}

const FUNDING_RATE_AVG_CLOSE_LENGTH = 10
const DERIVED_ANALYTICS_METRICS: ChartAnalyticsMetric[] = [
  'aggregatedFundingRateAvgClose',
  'cumulativeVolumeDelta',
]

export function getAnalyticsValues(
  candles: CandleList,
  metric: ChartAnalyticsMetric
): Array<number | string> {
  if (metric === 'buySellLiq') {
    return candles.buyLiqBase.map((buyValue, index) => {
      const sellValue = candles.sellLiqBase[index]
      const buy = typeof buyValue === 'number' ? buyValue : Number(buyValue)
      const sell = typeof sellValue === 'number' ? sellValue : Number(sellValue)

      if (Number.isFinite(buy)) return buy
      if (Number.isFinite(sell)) return -Math.abs(sell)
      return NaN
    })
  }

  if (metric === 'aggregatedFundingRateAvgClose') {
    return candles.fundingRateClose.map((_, index) => {
      const window = candles.fundingRateClose
        .slice(Math.max(0, index - FUNDING_RATE_AVG_CLOSE_LENGTH + 1), index + 1)
        .filter((value) => Number.isFinite(value))

      if (window.length === 0) return NaN
      return window.reduce((sum, value) => sum + value, 0) / window.length
    })
  }

  if (metric === 'cumulativeVolumeDelta') {
    let cumulativeValue = 0
    let baseline: number | null = null
    return candles.netBuyVolumeBase.map((rawValue) => {
      const value = toNumericValue(rawValue)
      if (value === null) return NaN
      cumulativeValue += value
      baseline ??= cumulativeValue
      return cumulativeValue - baseline
    })
  }

  const key = CHART_ANALYTICS_DATA_KEYS[metric]
  if (!key) return []
  return (candles[key] as Array<number | string> | undefined) ?? []
}

export function toNumericValue(value: number | string | undefined | null): number | null {
  if (value === undefined || value === null) return null

  const numeric = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

export function buildChartAnalyticsSeries(
  candles: CandleList | undefined,
  metric: ChartAnalyticsMetric
): ChartAnalyticsSeries {
  if (!candles) {
    return { metric, points: [] }
  }

  const values = getAnalyticsValues(candles, metric)
  const points: ChartAnalyticsPoint[] = []

  for (let i = 0; i < candles.ticks.length; i++) {
    const timeSeconds = Number(candles.ticks[i])
    const value = toNumericValue(values[i])

    if (!Number.isFinite(timeSeconds) || value === null) continue

    points.push({
      time: timeSeconds,
      value,
    })
  }

  return { metric, points }
}

export function buildAllChartAnalyticsSeries(
  candles: CandleList | undefined
): Record<ChartAnalyticsMetric, ChartAnalyticsSeries> {
  const metrics = [
    ...(Object.keys(CHART_ANALYTICS_DATA_KEYS) as ChartAnalyticsMetric[]),
    ...DERIVED_ANALYTICS_METRICS,
  ]

  return Object.fromEntries(
    metrics.map((metric) => [metric, buildChartAnalyticsSeries(candles, metric)])
  ) as Record<ChartAnalyticsMetric, ChartAnalyticsSeries>
}

export function getLatestAnalyticsValue(
  candles: CandleList | undefined,
  metric: ChartAnalyticsMetric
): number | null {
  const series = buildChartAnalyticsSeries(candles, metric)
  return series.points.length > 0 ? series.points[series.points.length - 1].value : null
}
